import { Employee, SavedHeaders } from './property-list.models';

export const defaultSavedHeaders: SavedHeaders = {
  addressDetails: [],
  propertyManagerHeaders: [],
  technicalAssetsManagerHeaders: [],
  sevManagerHeaders: [],
  accountantHeaders: [],
  caretakerHeaders: [],
  technicalServiceHeaders: [],
  maxPropertyManagerCols: 0,
  maxTechnicalAssetsManagerCols: 0,
  maxSevManagerCols: 0,
  maxAccountantCols: 0,
};

export const MANAGER_KEY_MAPPING: { [key: string]: keyof Employee } = {
  "PROPERTY_MANAGER_NAME": "name",
  "PROPERTY_MANAGER_PHONE": "phone",
  "PROPERTY_MANAGER_EMAIL": "email"
};

export const TECHNICAL_ASSETS_MANAGER_KEY_MAPPING: { [key: string]: keyof Employee } = {
  "TECHNICAL_ASSETS_MANAGER_NAME": "name",
  "TECHNICAL_ASSETS_MANAGER_PHONE": "phone",
  "TECHNICAL_ASSETS_MANAGER_EMAIL": "email"
};

export const SEV_MANAGER_KEY_MAPPING: { [key: string]: keyof Employee } = {
  "CONDOMINIUM_MANAGER_NAME": "name",
  "CONDOMINIUM_MANAGER_PHONE": "phone",
  "CONDOMINIUM_MANAGER_EMAIL": "email",
  "CONDOMINIUM_MANAGER_MOBILE": "mobile"
};

export const ACCOUNTANT_KEY_MAPPING: { [key: string]: keyof Employee } = {
  "ACCOUNTANT_NAME": "name",
  "ACCOUNTANT_PHONE": "phone",
  "ACCOUNTANT_EMAIL": "email",
  "ACCOUNTANT_MOBILE": "mobile"
};

export const SERVICE_TYPE_TRANSLATION_KEYS: { [key: string]: string } = {
  'Winter Service': 'WINTER_SERVICE',
  'Heating': 'HEATING',
  'Sanitary': 'SANITARY',
  'Electrical': 'ELECTRICAL',
  'Smoke Detector': 'SMOKE_DETECTOR',
  'Elevator': 'ELEVATOR',
  'Garage Door': 'GARAGE_DOOR',
  'Pipe Blockage': 'PIPE_BLOCKAGE',
  'Pipe Burst & Water Damage': 'PIPE_BURST_WATER_DAMAGE',
  'Cable Television': 'CABLE_TELEVISION',
  'Stairwell Cleaning': 'STAIRWELL_CLEANING',
  'Locksmith Service': 'LOCKSMITH_SERVICE',
  'Chimney Sweep': 'CHIMNEY_SWEEP',
  'Meter Reading Service': 'METER_READING_SERVICE',
  'Bulk Waste Collection': 'BULK_WASTE_COLLECTION',
  'Gas Supply/Emergency Service': 'GAS_SUPPLY_EMERGENCY_SERVICE',
  'Power Supply/Emergency Service': 'POWER_SUPPLY_EMERGENCY_SERVICE',
  'Water Supply': 'WATER_SUPPLY',
  'Police': 'POLICE',
  'Garden Maintenance': 'GARDEN_MAINTENANCE',
  'Pest Control': 'PEST_CONTROL',
  'Energy Supply': 'ENERGY_SUPPLY',
  'Waste Disposal': 'WASTE_DISPOSAL',
  'Building Insurance': 'BUILDING_INSURANCE',
  'Fiber / Internet': 'FIBER_INTERNET',
  'Gas Emergency/Gas Odor': 'GAS_EMERGENCY_GAS_ODOR',
  'Recyclable Waste Disposal': 'RECYCLABLE_WASTE_DISPOSAL',
  'Heating Oil Supply': 'HEATING_OIL_SUPPLY',
  'District Heating Supplier': 'DISTRICT_HEATING_SUPPLIER',
  'Lifting Systems and Pumps': 'LIFTING_SYSTEMS_AND_PUMPS',
  'Electric Storage Heater': 'ELECTRIC_STORAGE_HEATER',
  'Locking System Maintenance': 'LOCKING_SYSTEM_MAINTENANCE',
  'Green Roof Maintenance': 'GREEN_ROOF_MAINTENANCE',
  'Rental Mat Service': 'RENTAL_MAT_SERVICE',
  'Local Heating Supplier': 'LOCAL_HEATING_SUPPLIER',
  'Street and Sidewalk Cleaning': 'STREET_AND_SIDEWALK_CLEANING',
  'Double Parker': 'DOUBLE_PARKER',
  'Gate/Gate Systems': 'GATE_GATE_SYSTEMS',
  'Sewer/Blockage': 'SEWER_BLOCKAGE',
  'Solar System/PVA': 'SOLAR_SYSTEM_PVA',
  'Ventilation': 'VENTILATION',
  'Apartment Transfer Station (DHW/Heating)': 'APARTMENT_TRANSFER_STATION_DHW_HEATING',
  'Smoke and Heat Extraction System': 'SMOKE_AND_HEAT_EXTRACTION_SYSTEM',
  'Fire Extinguisher': 'FIRE_EXTINGUISHER',
  'Fire Doors': 'FIRE_DOORS',
  'Fire Alarm System': 'FIRE_ALARM_SYSTEM',
  'Heating Cost Allocator': 'HEATING_COST_ALLOCATOR',
  'WE Meter': 'WE_METER',
  'Coating (UG)': 'COATING_UG',
  'Windows': 'WINDOWS',
  'Roller Shutter/Raffstore': 'ROLLER_SHUTTER_RAFFSTORE',
  'Doors': 'DOORS',
  'Roofer': 'ROOFER',
  'Property Developer': 'PROPERTY_DEVELOPER',
  'Green Pruning': 'GREEN_PRUNING',
};

export const HOSPITALITY_LIMITS = {
  title: 120,
  summary: 500,
  details: 80,
  instructions: 500,
  rule: 120,
  location: 240,
  contact: 240,
  linkLabel: 120,
  linkUrl: 500,
  digitalKeyCode: 80,
  digitalKeyLink: 500,
  digitalKeyNotes: 500,
  checklistTitle: 120,
  checklistItem: 80,
  infoSectionBody: 80,
} as const;

// --- NEW: dynamic step configs (keep titles fully i18n via existing keys) ---
export const HOSPITALITY_SECTION_ORDER: Array<{ key: string; titleKey: string }> = [
  { key: 'wifi',            titleKey: 'HOSPITALITY_FIELDS.WIFI_TITLE' },
  { key: 'hostContact',     titleKey: 'HOSPITALITY_FIELDS.HOST_CONTACT_TITLE' },
  { key: 'checkIn',         titleKey: 'HOSPITALITY_FIELDS.CHECK_IN_TITLE' },
  { key: 'checkOut',        titleKey: 'HOSPITALITY_FIELDS.CHECK_OUT_TITLE' },
  { key: 'houseRules',      titleKey: 'HOSPITALITY_FIELDS.HOUSE_RULES_TITLE' },
  { key: 'applianceGuides', titleKey: 'HOSPITALITY_FIELDS.APPLIANCE_GUIDES_TITLE' },
  { key: 'localTips',       titleKey: 'HOSPITALITY_FIELDS.LOCAL_TIPS_TITLE' },
  { key: 'infoSections',    titleKey: 'HOSPITALITY_FIELDS.INFO_SECTIONS_TITLE' },
];

// --- NEW: group hospitality sections into fewer wizard steps ---
export const HOSPITALITY_GROUPS: Array<{
  key: string;
  titleKey: string;        // i18n key or plain text; you can add these to your translations later
  sections: string[];      // keys from HOSPITALITY_SECTION_ORDER
}> = [
  {
    key: 'essentials',
    titleKey: 'PROPERTY_LIST.HOSP_GROUP_ESSENTIALS',  // e.g. "Essentials"
    sections: ['wifi', 'hostContact', 'checkIn', 'checkOut']
  },
  {
    key: 'house_and_guides',
    titleKey: 'PROPERTY_LIST.HOSP_GROUP_HOUSE_AND_GUIDES', // e.g. "House & Guides"
    sections: ['houseRules', 'applianceGuides']
  },
  {
    key: 'local_and_info',
    titleKey: 'PROPERTY_LIST.HOSP_GROUP_LOCAL_AND_INFO', // e.g. "Local tips & info"
    sections: ['localTips', 'infoSections']
  }
];

/** flip to false if you want 1-section-per-step again */
export const USE_GROUPED_HOSP_STEPS = true;
